// Helper pour récupérer le token JWT Supabase côté client
// Utilise le singleton supabase — pas de createClient() ici
import { supabase } from './supabase';

export async function getSupabaseToken() {
  try {
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

// 🆕 MULTI-TENANT — Endpoints qui supportent ?clientId=xxx
// Liste à étendre si on ajoute de nouvelles routes scopées par client.
const MULTI_TENANT_ENDPOINTS = [
  '/api/employees',
  '/api/fiches-paie',
  '/api/declarations',
  '/api/payroll',
  '/api/dimona',
  // ⚠️ 14/08/2026 — `/api/onss/dimona` est une AUTRE adresse que `/api/dimona`,
  // et c'est par elle que déclarent les écrans Flexi et Extras Horeca. Elle
  // n'était pas dans la liste : le clientId n'y était donc jamais injecté, et
  // la déclaration partait sans employeur — donc sous le matricule d'Aureus.
  // Le mécanisme était bon, l'adresse manquait.
  '/api/onss/dimona',
  // ⚠️ 23/08/2026 — TROISIÈME adresse du même mécanisme. L'écran « Embauche
  // rapide 5s » prépare sa Dimona par `/api/embauche-express/dimona-preview`,
  // qui ne figurait pas ici : la préparation partait donc sans employeur.
  // Depuis que le repli silencieux sur le matricule d'Aureus est supprimé
  // (14/08), cette absence ne se voyait plus comme une fausse déclaration mais
  // comme un refus — « BCE de l'employeur manquant » sur des dossiers dont la
  // BCE est pourtant remplie.
  '/api/embauche-express/dimona-preview',
];

// Lit le client actif depuis localStorage. Retourne null si pas de client
// actif (mode global Aureus IA SRL interne).
// Exportée depuis le 2026-08-07 : tracer-document.js en avait besoin et allait
// la réécrire. Une deuxième implémentation aurait re-créé le bug des clés
// `_guest` corrigé ci-dessous (client affiché ≠ client appelé, incident du
// 01/08/2026). Une seule lecture du client actif dans toute l'app.
export function getActiveClientId() {
  if (typeof localStorage === 'undefined') return null;
  try {
    // Cherche d'abord la cle scopee par user (forme moderne).
    // ⚠️ Les cles `..._guest` sont ecrites quand un client est choisi AVANT que la
    // session soit chargee. Elles survivent, et comme on renvoyait la PREMIERE cle
    // trouvee, une vieille cle `_guest` primait sur la vraie : l'ecran affichait un
    // client et les appels API partaient avec l'identifiant d'un autre (incident du
    // 01/08/2026). On les relegue donc en dernier recours.
    const scoped = Object.keys(localStorage).filter(k => k.startsWith('aureus_active_client_'));
    const keys = scoped.filter(k => k !== 'aureus_active_client_guest')
      .concat(scoped.filter(k => k === 'aureus_active_client_guest'));
    for (const k of keys) {
      const raw = localStorage.getItem(k);
      if (raw) {
        const cl = JSON.parse(raw);
        if (cl?.id) return cl.id;
      }
    }
    // Fallback : ancienne cle non-scopee
    const raw = localStorage.getItem('aureus_active_client');
    if (raw) {
      const cl = JSON.parse(raw);
      if (cl?.id) return cl.id;
    }
  } catch {}
  return null;
}

// Ajoute ?clientId=xxx a l'URL si l'endpoint le supporte ET qu'un client est actif.
// Ne touche pas aux URLs qui ont deja un clientId (respect du caller explicite).
// Exportee le 23/08/2026 : EmbaucheExpress a son PROPRE fetch (il doit laisser
// le navigateur poser le boundary multipart de l'upload eID, ce qu'authFetch ne
// permet pas) et n'empruntait donc aucune de ces lignes. Une deuxieme
// implementation de l'injection aurait re-cree le meme ecart plus tard : le
// scope de l'appel se decide ICI, pour tout appelant.
export function injectClientId(url) {
  if (typeof url !== 'string') return url;
  // Verifier si l'URL correspond a un endpoint multi-tenant
  const isMultiTenant = MULTI_TENANT_ENDPOINTS.some(ep => {
    // Match path exact ou path?... ou path/...
    return url === ep
      || url.startsWith(ep + '?')
      || url.startsWith(ep + '/')
      || url.startsWith(ep + '#');
  });
  if (!isMultiTenant) return url;
  // Si clientId deja present (en query string), ne pas reinjecter
  if (/[?&]client(?:_)?[Ii]d=/.test(url)) return url;
  const activeId = getActiveClientId();
  if (!activeId) return url;
  // Ajouter le param
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}clientId=${encodeURIComponent(activeId)}`;
}

export async function authFetch(url, options = {}) {
  let token = await getSupabaseToken();
  // Retry une fois si token absent (session pas encore chargée)
  if (!token) {
    await new Promise(r => setTimeout(r, 500));
    token = await getSupabaseToken();
  }
  // 🆕 Auto-inject clientId pour les endpoints multi-tenant
  // Uniquement sur GET (et requêtes sans method = GET par défaut). Les
  // POST/PUT/DELETE doivent passer client_id explicitement dans le body.
  const method = (options.method || 'GET').toUpperCase();
  const finalUrl = method === 'GET' ? injectClientId(url) : url;
  const isFormData = options.body instanceof FormData;
  return fetch(finalUrl, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}
